import { FeatureCollection } from "geojson";
import { sub } from "date-fns";

import i18n from "@/lib/i18n";
import {
  api,
  PublishedDashboard,
  SeismicEventsMapLayer as SeismicEventsLayerConfig,
} from "@/lib/api";
import Layer from "@/lib/map-layers/layer";
import { DimensionValueSet } from "@/lib/dimensions";
import { LayerSettingValueSet } from "@/lib/map-layers/layer-settings";
import { EnumLayerSetting } from "@/lib/map-layers/layer-settings/enum-layer-setting";
import {
  SeismicEventAgeColors,
  SeismicEventDateOptions,
  SeismicEventDepthColors,
  SeismicEventPropertyOptions,
} from "@/features/shared/widgets/map/map.types";
import { LegendListItem, LegendListTitle } from "@/features/shared/widgets/map/map.styles";

import SeismicEventsMarkers from "./seismic-events-markers";

export default class SeismicEventsLayer implements Layer {
  private config;
  private selectedDashboard;

  constructor(config: SeismicEventsLayerConfig, selectedDashboard: PublishedDashboard) {
    this.config = config;
    this.selectedDashboard = selectedDashboard;
  }

  private data: FeatureCollection | undefined;

  get id() {
    return this.config.id;
  }
  getTitle() {
    return this.config.title;
  }
  get dimensions() {
    return undefined;
  }
  getRelevantDimensionValues(): DimensionValueSet {
    return {};
  }

  get settings() {
    const typeOptions = [
      {
        key: "age",
        label: "Age",
        value: SeismicEventPropertyOptions.AGE,
      },
      {
        key: "depth",
        label: "Depth",
        value: SeismicEventPropertyOptions.DEPTH,
      },
    ];

    const historyOptions = [
      {
        key: "1day",
        label: i18n.t(`map.seismic_events.dates.${SeismicEventDateOptions.ONE_DAY}`),
        value: SeismicEventDateOptions.ONE_DAY,
      },
      {
        key: "1month",
        label: i18n.t(`map.seismic_events.dates.${SeismicEventDateOptions.ONE_MONTH}`),
        value: SeismicEventDateOptions.ONE_MONTH,
      },
      {
        key: "2months",
        label: i18n.t(`map.seismic_events.dates.${SeismicEventDateOptions.TWO_MONTHS}`),
        value: SeismicEventDateOptions.TWO_MONTHS,
      },
      {
        key: "3months",
        label: i18n.t(`map.seismic_events.dates.${SeismicEventDateOptions.THREE_MONTHS}`),
        value: SeismicEventDateOptions.THREE_MONTHS,
      },
    ];

    return [
      new EnumLayerSetting({
        id: "seismicEventsType",
        values: typeOptions,
        default: typeOptions[0],
      }),
      new EnumLayerSetting({
        id: "seismicEventsHistory",
        values: historyOptions,
        default: historyOptions[3],
      }),
    ];
  }

  async initialize() {
    try {
      const today = new Date();
      const start = sub(today, { months: 3 });

      const promise = await api.published.getPublishedMapLayerData(
        this.selectedDashboard.id,
        this.id,
        start.toISOString(),
        today.toISOString(),
      );
      this.data = promise.data as unknown as FeatureCollection;
    } catch (error) {
      this.data = undefined;
      throw error;
    }
  }

  async preload() {
    return undefined;
  }

  isActive() {
    return true;
  }

  isForecast() {
    return false;
  }

  isLoaded() {
    return this.data !== undefined;
  }

  hasLoadError(_dimensionValues: DimensionValueSet) {
    return false;
  }

  hasPartialLoadErrors() {
    return false;
  }

  clearLoadFailures() {
    // no-op
  }

  async loadLegendImage() {
    return undefined;
  }
  renderLegend(_: DimensionValueSet, settingValues: LayerSettingValueSet) {
    const selectedProperty = settingValues?.seismicEventsType;
    if (!selectedProperty) return undefined;

    return (
      <div>
        {selectedProperty.value === SeismicEventPropertyOptions.AGE && (
          <>
            <LegendListTitle>Earthquake age</LegendListTitle>
            <LegendListItem markerColor={SeismicEventAgeColors.HOURS}>
              {i18n.t("map.seismic_events.legend.ages.hours")}
            </LegendListItem>
            <LegendListItem markerColor={SeismicEventAgeColors.DAYS}>
              {i18n.t("map.seismic_events.legend.ages.days")}
            </LegendListItem>
            <LegendListItem markerColor={SeismicEventAgeColors.WEEKS}>
              {i18n.t("map.seismic_events.legend.ages.weeks")}
            </LegendListItem>
            <LegendListItem markerColor={SeismicEventAgeColors.MONTHS}>
              {i18n.t("map.seismic_events.legend.ages.months")}
            </LegendListItem>
          </>
        )}
        {selectedProperty.value === SeismicEventPropertyOptions.DEPTH && (
          <>
            <LegendListTitle>Earthquake depth (km)</LegendListTitle>
            <LegendListItem markerColor={SeismicEventDepthColors.LEVEL_1}>
              0 - 15
            </LegendListItem>
            <LegendListItem markerColor={SeismicEventDepthColors.LEVEL_2}>
              16 - 35
            </LegendListItem>
            <LegendListItem markerColor={SeismicEventDepthColors.LEVEL_3}>
              36 - 70
            </LegendListItem>
            <LegendListItem markerColor={SeismicEventDepthColors.LEVEL_4}>
              71 - 150
            </LegendListItem>
            <LegendListItem markerColor={SeismicEventDepthColors.LEVEL_5}>
              {"> 150"}
            </LegendListItem>
          </>
        )}
      </div>
    );
  }

  renderLayer() {
    return undefined;
  }

  renderComponents(_: DimensionValueSet, settingValues: LayerSettingValueSet) {
    if (!this.data) return null;

    return (
      <SeismicEventsMarkers
        data={this.data}
        layer={this.config}
        currentSettingValues={settingValues}
      />
    );
  }
}
